import { useCallback, useEffect, useRef, type RefObject } from 'react';
import {
  mergeShortcutBindings,
  type ShortcutActionId,
  type ShortcutBindings,
} from '@core/shortcuts/bindings.ts';
import {
  isEditableKeyboardTarget,
  matchShortcut,
} from '@core/shortcuts/matchShortcut.ts';
import {
  nudgeSubYByDelta,
  resolveSubYKey,
  snapSubY,
  type SubYStorageKey,
} from '@core/layout.ts';
import { DEFAULT_SETTINGS, type Settings } from '@core/settings/schema.ts';
import type { Cue } from '@core/model/cue.ts';
import type { SettingsStore } from '@ports/SettingsStore.ts';
import type { VideoController } from '@ports/VideoController.ts';
import { findCueSeekTarget, type CueSeekKind } from '../lib/cueSeek.ts';
import { slotHasTranslationLang, toggleDualMode } from '../lib/displayMode.ts';
import type { SubtitleStore } from '../state/subtitleStore.ts';
import {
  SHORTCUT_DELAY_STEP_MS,
  SHORTCUT_FONT_MAX,
  SHORTCUT_FONT_MIN,
  SHORTCUT_FONT_STEP,
  SHORTCUT_SUB_Y_NUDGE_PX,
} from '../lib/shortcutSteps.ts';

const MOBILE_SHORTCUT_ACTIONS: ShortcutActionId[] = [
  'seekPrev',
  'seekRepeat',
  'seekNext',
  'delayBothDown',
  'delayBothUp',
  'delayMainDown',
  'delayMainUp',
  'delaySecondDown',
  'delaySecondUp',
  'toggleMain',
  'toggleSecond',
  'dualMain',
  'dualSecond',
  'copy',
];

function stripHtml(text: string): string {
  return text.replace(/<br\s*\/?>/gi, ' ').replace(/\s+/g, ' ').trim();
}

async function copyTextToClipboard(text: string): Promise<void> {
  try {
    await navigator.clipboard.writeText(text);
    return;
  } catch {
    const ta = document.createElement('textarea');
    ta.value = text;
    ta.style.position = 'fixed';
    ta.style.left = '-9999px';
    document.body.appendChild(ta);
    ta.select();
    document.execCommand('copy');
    ta.remove();
  }
}

function activeCueTextAtVideoTime(
  store: SubtitleStore,
  video: VideoController,
): string {
  for (const slot of ['main', 'second'] as const) {
    const slotState = store.getSlot(slot);
    const cues = slotState.cues;
    if (!cues?.length) continue;
    const timeMs = video.getCurrentTimeMs() + slotState.view.delayMs;
    const cue = cues.find((c: Cue) => c.start <= timeMs && timeMs <= c.end);
    if (cue) {
      return stripHtml(cue.kuroshiro ?? cue.text ?? '');
    }
  }
  return '';
}

function clampFont(n: number): number {
  return Math.min(SHORTCUT_FONT_MAX, Math.max(SHORTCUT_FONT_MIN, Math.round(n * 100) / 100));
}

function readSettings(settingsStore: SettingsStore): Settings {
  return settingsStore.getSnapshot?.() ?? DEFAULT_SETTINGS;
}

export type UsePlayerShortcutsOptions = {
  video: VideoController | null;
  store: SubtitleStore;
  settingsStore: SettingsStore;
  rootRef: RefObject<HTMLElement | null>;
  ready: boolean;
};

/**
 * Keyboard shortcuts when enableShortcuts is on and the player shell is focused.
 */
export function usePlayerShortcuts({
  video,
  store,
  settingsStore,
  rootRef,
  ready,
}: UsePlayerShortcutsOptions): void {
  const settingsRef = useRef(readSettings(settingsStore));
  settingsRef.current = readSettings(settingsStore);

  useEffect(() => {
    return settingsStore.subscribe(() => {
      settingsRef.current = readSettings(settingsStore);
    });
  }, [settingsStore]);

  const seek = useCallback(
    (kind: CueSeekKind) => {
      if (!video) return;
      const main = store.getSlot('main');
      const second = store.getSlot('second');
      const cues = main.cues?.length ? main.cues : second.cues;
      if (!cues?.length) return;
      const delayMs = (main.cues?.length ? main : second).view.delayMs;
      const timeMs = video.getCurrentTimeMs() + delayMs;
      const target = findCueSeekTarget(cues, timeMs, kind);
      if (target) video.seekMs(target.start);
    },
    [video, store],
  );

  const nudgeDelay = useCallback(
    (slot: 'main' | 'second' | 'both', delta: number) => {
      const slots = slot === 'both' ? (['main', 'second'] as const) : ([slot] as const);
      for (const s of slots) {
        const cur = store.getSlot(s).view.delayMs;
        store.setViewState(s, { delayMs: Math.round(cur + delta) });
      }
    },
    [store],
  );

  const nudgeSubY = useCallback(
    (dir: 1 | -1) => {
      if (!video) return;
      const settings = settingsRef.current;
      const separation = Boolean(settings.subtitleSeparation);
      const rect = video.getViewportRect();
      const keys: SubYStorageKey[] = separation
        ? ['mainSubY', 'secondSubY']
        : [resolveSubYKey('main', false)];
      const patch: Partial<Record<SubYStorageKey, number>> = {};
      for (const key of keys) {
        patch[key] = snapSubY(
          nudgeSubYByDelta(settings[key], dir * SHORTCUT_SUB_Y_NUDGE_PX, rect),
        );
      }
      void settingsStore.set(patch);
    },
    [video, settingsStore],
  );

  const nudgeFont = useCallback(
    (slot: 'main' | 'second', dir: 1 | -1) => {
      const settings = settingsRef.current;
      if (slot === 'main') {
        void settingsStore.set({
          mainFontSizeRatio: clampFont(settings.mainFontSizeRatio + dir * SHORTCUT_FONT_STEP),
        });
      } else {
        void settingsStore.set({
          secondFontSizeRatio: clampFont(settings.secondFontSizeRatio + dir * SHORTCUT_FONT_STEP),
        });
      }
    },
    [settingsStore],
  );

  useEffect(() => {
    const root = rootRef.current;
    if (!root || !ready) return;

    const onKeyDown = (event: KeyboardEvent) => {
      if (isEditableKeyboardTarget(event.target)) return;
      const settings = settingsRef.current;
      if (!settings.enableShortcuts) return;

      const main = store.getSlot('main');
      const second = store.getSlot('second');
      if (!main.cues?.length && !second.cues?.length) return;

      const bindings: ShortcutBindings = mergeShortcutBindings(settings.shortcutBindings);
      const hit = (id: ShortcutActionId) => matchShortcut(event, bindings[id]);

      if (MOBILE_SHORTCUT_ACTIONS.some((id) => hit(id))) {
        event.preventDefault();
      } else if (event.key === 'ArrowUp') {
        event.preventDefault();
        nudgeSubY(-1);
        return;
      } else if (event.key === 'ArrowDown') {
        event.preventDefault();
        nudgeSubY(1);
        return;
      } else if (event.key === '[') {
        event.preventDefault();
        nudgeFont('main', -1);
        return;
      } else if (event.key === ']') {
        event.preventDefault();
        nudgeFont('main', 1);
        return;
      } else if (event.key === '{') {
        event.preventDefault();
        nudgeFont('second', -1);
        return;
      } else if (event.key === '}') {
        event.preventDefault();
        nudgeFont('second', 1);
        return;
      } else {
        return;
      }

      if (hit('seekPrev')) seek('prev');
      if (hit('seekRepeat')) seek('repeat');
      if (hit('seekNext')) seek('next');
      if (hit('delayBothDown')) nudgeDelay('both', -SHORTCUT_DELAY_STEP_MS);
      if (hit('delayBothUp')) nudgeDelay('both', SHORTCUT_DELAY_STEP_MS);
      if (hit('delayMainDown')) nudgeDelay('main', -SHORTCUT_DELAY_STEP_MS);
      if (hit('delayMainUp')) nudgeDelay('main', SHORTCUT_DELAY_STEP_MS);
      if (hit('delaySecondDown')) nudgeDelay('second', -SHORTCUT_DELAY_STEP_MS);
      if (hit('delaySecondUp')) nudgeDelay('second', SHORTCUT_DELAY_STEP_MS);
      if (hit('toggleMain')) {
        const cur = store.getSlot('main').view.hidden;
        store.setViewState('main', { hidden: !cur });
      }
      if (hit('toggleSecond')) {
        const cur = store.getSlot('second').view.hidden;
        store.setViewState('second', { hidden: !cur });
      }
      if (hit('dualMain')) {
        toggleDualMode(
          store,
          'main',
          slotHasTranslationLang(settingsRef.current, 'main'),
        );
      }
      if (hit('dualSecond')) {
        toggleDualMode(
          store,
          'second',
          slotHasTranslationLang(settingsRef.current, 'second'),
        );
      }
      if (hit('copy') && video) {
        const text = activeCueTextAtVideoTime(store, video);
        if (text) void copyTextToClipboard(text);
      }
    };

    root.addEventListener('keydown', onKeyDown);
    return () => root.removeEventListener('keydown', onKeyDown);
  }, [rootRef, ready, store, seek, nudgeDelay, nudgeSubY, nudgeFont]);
}
